// AdminSurveyDetailPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
} from "chart.js";
import { Radar } from "react-chartjs-2";
import { Bar } from "react-chartjs-2";
import "./AdminSurveyDetailPage.css";
import WaffleChart from "../components/WaffleChart";
import DepartmentGroupBarCompare from "../components/DepartmentDetailPage.jsx";
import MultiDepartmentAgreementPie from "../components/MultiDepartmentAgreementPie.jsx";
import "../components/analyticsCards.css";
import { useAuth } from '../context/AuthContext';


ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler);



// color palette
const POS_COLOR = "#A81026"; // green
const NEG_COLOR = "#F3AC26"; // red

const AdminSurveyDetailPage = ({ surveyId: propSurveyId, companyId }) => {

  const params = useParams();
  const navigate = useNavigate();
  console.log(`Slected compnay at admin survey is details is ${companyId}`)

  // ✅ surveyId now works in both cases
  const surveyId = propSurveyId || params.surveyId;

  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState(null);
  const [overallMode, setOverallMode] = useState("posneg"); 
  const { user } = useAuth();
// "posneg" | "likert"


  useEffect(() => {
    fetchAnalytics();
    fetchSurveyStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/adminanalytics/${surveyId}/${companyId}`
      );
      setSurveyData(res.data);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Failed to fetch analytics");
      setSurveyData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/reportexcel/responses/${surveyId}/${companyId}`
    );

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `Survey_${surveyId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);

  } catch (err) {
    alert("Failed to download Excel");
  }
};


  const fetchSurveyStatus = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/survey-company/status/${surveyId}`
      );
      setSurveyStatus(res.data.status);
      setIsExpired(res.data.expired);
    } catch (err) {
      console.error("Error fetching survey status:", err);
    }
  };




  const toggleSurveyStatus = async () => {
    if (isExpired) return;
    const newStatus = surveyStatus === "active" ? "inactive" : "active";
    try {
      setUpdatingStatus(true);
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/survey-company/status/${surveyId}`,
        { status: newStatus , companyId: companyId}
      );
      setSurveyStatus(newStatus);
    } catch (err) {
      console.error("Error toggling survey status:", err);
      alert("Failed to update survey status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

    const hasResponses = useMemo(() => {
  if (!surveyData) return false;

  return (
    (surveyData.group_polarity_summary?.length || 0) > 0 ||
    (surveyData.department_group_summary?.length || 0) > 0 ||
    (surveyData.department_comparison?.length || 0) > 0 ||
    safeNum(surveyData.overall_polarity_summary?.positive_count) > 0 ||
    safeNum(surveyData.overall_polarity_summary?.negative_count) > 0
  );
}, [surveyData]);

  const noResponses =
  !surveyData?.group_polarity_summary?.length &&
  !surveyData?.department_group_summary?.length &&
  !surveyData?.department_comparison?.length;

  const waffleTotalData = useMemo(() => {
  if (
    !surveyData?.department_response_completion_summary ||
    !surveyData?.response_completion_summary
  ) {
    return [];
  }

  const expected =
    surveyData.response_completion_summary.total_expected_responses;

  const totalQuestions =
    surveyData.response_completion_summary.total_questions;

  return surveyData.department_response_completion_summary.map(d => ({
    department: d.department,
    level: d.level || "All",

    // 🔑 percentage used for waffle fill
    percent_of_total_possible:
      totalQuestions > 0
        ? (d.actual_responses / totalQuestions) * 100
        : 0,

    // 🔑 counts (used for labels + warnings)
    actual_responses: d.actual_responses,
    response_expected: expected
  }));
}, [surveyData]);



// ------------------------
// TEXTBOX RESPONSE VIEWER
// ------------------------
const TextboxResponseViewer = ({ textboxData }) => {

  // ✅ SAFETY FALLBACKS (always defined first)
  const safeData = Array.isArray(textboxData) ? textboxData : [];

  // ✅ EXTRACT FILTER OPTIONS FIRST
  const allDepartments = safeData.map(d => d.department);
  const allLevels = [
    ...new Set(safeData.flatMap(d => d.levels?.map(l => l.level) || []))
  ];
  const allGroups = [
    ...new Set(
      safeData.flatMap(d =>
        d.levels?.flatMap(l => l.groups?.map(g => g.group_name) || []) || []
      )
    )
  ];

  // ✅ HOOKS FIRST — ALWAYS
  const [selectedDepts, setSelectedDepts] = useState(allDepartments);
  const [selectedLevels, setSelectedLevels] = useState(allLevels);
  const [selectedGroups, setSelectedGroups] = useState(allGroups);

  // ✅ SAFE NOW
  if (!safeData.length) return null;

  const toggle = (list, setter, value) =>
    setter(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);

  return (
    <div className="textbox-viewer-wrapper">

      {/* <h2 className="analytics-title">📝 Text Responses</h2> */}

      {/* FILTERS */}
      <div className="analytics-layout">


          <div className="dgc-panel">
          <div className="dgc-panel-title">Departments</div>
  

          <div className="dgc-checkbox-list">
              {allDepartments.map(dept => (
            <label key={dept}>
              <input
                type="checkbox"
                checked={selectedDepts.includes(dept)}
                onChange={() => toggle(selectedDepts, setSelectedDepts, dept)}
              /> {dept}
            </label>
          ))}
          </div>
        </div>



          <div className="dgc-panel">
          <div className="dgc-panel-title">Levels</div>

          <div className="dgc-checkbox-list">
            {allLevels.map(level => (
            <label key={level}>
              <input
                type="checkbox"
                checked={selectedLevels.includes(level)}
                onChange={() => toggle(selectedLevels, setSelectedLevels, level)}
              /> {level}
            </label>
          ))}
          </div>
        </div>




               <div className="dgc-panel">
          <div className="dgc-panel-title">Groups</div>

          <div className="dgc-checkbox-list">
            {allGroups.map(group => (
            <label key={group}>
              <input
                type="checkbox"
                checked={selectedGroups.includes(group)}
                onChange={() => toggle(selectedGroups, setSelectedGroups, group)}
              /> {group}
            </label>
          ))}
          </div>
        </div>
        


      </div>

      {/* DATA DISPLAY */}
      {safeData
        .filter(d => selectedDepts.includes(d.department))
        .map(deptBlock => (
          <div key={deptBlock.department} className="analytics-card">

            <h3>🏢 {deptBlock.department}</h3>

            {deptBlock.levels
              .filter(l => selectedLevels.includes(l.level))
              .map(levelBlock => (
                <div key={levelBlock.level}>

                  <h4>🎓 Level: {levelBlock.level}</h4>

                  {levelBlock.groups
                    .filter(g => selectedGroups.includes(g.group_name))
                    .map(group => (
                      <div key={group.group_name}>

                        <h4>📌 {group.group_name}</h4>

                       {Object.values(
  group.questions.reduce((acc, q) => {
    const qid = q.question_id;

    if (!acc[qid]) {
      acc[qid] = {
        question: q.question,
        answers: []
      };
    }

    let ans = q.selected_answer;
    try {
      if (typeof ans === "string") ans = JSON.parse(ans);
    } catch {}

    const answers = Array.isArray(ans) ? ans : [ans];
    acc[qid].answers.push(...answers);

    return acc;
  }, {})
).map((qBlock, idx) => (
  <div key={idx} className="textbox-result-card">
    <div className="textbox-q">
      Q: {qBlock.question}
    </div>

    {qBlock.answers.map((a, i) => (
      <div key={i} className="textbox-answer">
        ➤ {a}
      </div>
    ))}
  </div>
))}

                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}
    </div>
  );
};






const renderRadarChart = () => {
  if (!surveyData?.group_polarity_summary) return null;

  const groups = surveyData.group_polarity_summary.filter(g => g.num_questions > 0);;

  const labels = groups.map(g => g.group);
  const positive = groups.map(g => g.positive_percent || 0);
  const negative = groups.map(g => g.negative_percent || 0);

  const data = {
    labels,
        datasets: [
      {
        label: "Positive %",
        data: positive,
        backgroundColor: "rgba(76, 132, 255, 0.25)",
        borderColor: "#4C84FF",
        borderWidth: 2,
        pointBackgroundColor: "#4C84FF"
      },
      {
        label: "Negative %",
        data: negative,
        backgroundColor: "rgba(255, 99, 132, 0.25)",
        borderColor: "#FF6384",
        borderWidth: 2,
        pointBackgroundColor: "#FF6384"
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: { showLabelBackdrop: false, stepSize: 20 },
        pointLabels: {
          font: { size: 14, weight: 600 },
          color: "#333"
        }
      }
    },
    plugins: {
      legend: { display: false } // we hide Chart.js legend
    }
  };

  return (
    <div style={{ display: "flex", gap: 40, alignItems: "center", marginTop: 30 }}>
      
      {/* Radar Chart */}
      <div className="chart-box" style={{ height: 500, flex: 1 }}>
        <Radar data={data} options={options} />
      </div>

      {/* Custom RIGHT-SIDE LEGEND */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        background: "#fff",
        padding: "15px 18px",
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        minWidth: "240px"
      }}>
        <h3 style={{ margin: 0, fontSize: 17 }}>Group Summary</h3>

        {groups.map((g, idx) => (
          <div key={idx} style={{
            padding: "8px 0",
            borderBottom: "1px dashed #ddd"
          }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{g.group}</div>
            <div style={{ fontSize: 14, color: "#4C84FF" }}>
              Positive: <strong>{g.positive_percent}%</strong>
            </div>
            <div style={{ fontSize: 14, color: "#E15759" }}>
              Negative: <strong>{g.negative_percent}%</strong>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

const DepartmentComparisonRadar = ({ summary }) => {
  const departments = summary.map((d) => d.department);
  const groups = summary[0]?.groups.map((g) => g.group) || [];

  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState(groups);
  const [mode, setMode] = useState("both"); // positive | negative | both

  // toggle dept
  const toggleDept = (dept) => {
    setSelectedDepts((p) =>
      p.includes(dept) ? p.filter((d) => d !== dept) : [...p, dept]
    );
  };

  // toggle group
  const toggleGroup = (grp) => {
    setSelectedGroups((p) =>
      p.includes(grp) ? p.filter((g) => g !== grp) : [...p, grp]
    );
  };

  // prepare chart data
  const chartData = {
    labels: selectedGroups,
    datasets: selectedDepts.map((dept, idx) => {
      const deptData = summary.find((d) => d.department === dept);

      const data = selectedGroups.map((grp) => {
        const g = deptData.groups.find((x) => x.group === grp);
        if (!g) return 0;

        if (mode === "positive") return g.positive_percent;
        if (mode === "negative") return g.negative_percent;
        return g.positive_percent; // for "both", we use radar on positive
      });

      const colors = ["#4C84FF", "#FF6384", "#00B894", "#E17055", "#6C5CE7"];
      return {
        label: dept,
        data,
        backgroundColor: colors[idx % colors.length] + "33",
        borderColor: colors[idx % colors.length],
        borderWidth: 2,
        pointBackgroundColor: colors[idx % colors.length],
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: { stepSize: 20 },
      },
    },
  };

  return (
    <div style={{ marginTop: 40 }}>
      <h2>📊 Department Comparison (Dynamic)</h2>

      {/* Filters Section */}
      <div
        style={{
          display: "flex",
          gap: 40,
          marginBottom: 30,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* Departments */}
        <div>
          <h3>Departments</h3>
          {departments.map((dept) => (
            <label key={dept} style={{ display: "block" }}>
              <input
                type="checkbox"
                checked={selectedDepts.includes(dept)}
                onChange={() => toggleDept(dept)}
              />{" "}
              {dept}
            </label>
          ))}
        </div>

        {/* Groups */}
        <div>
          <h3>Groups</h3>
          {groups.map((grp) => (
            <label key={grp} style={{ display: "block" }}>
              <input
                type="checkbox"
                checked={selectedGroups.includes(grp)}
                onChange={() => toggleGroup(grp)}
              />{" "}
              {grp}
            </label>
          ))}
        </div>

        {/* Mode Selector */}
        <div>
          <h3>Compare</h3>

          <label>
            <input
              type="radio"
              name="mode"
              checked={mode === "positive"}
              onChange={() => setMode("positive")}
            />
            Positive Only
          </label>
          <br />

          <label>
            <input
              type="radio"
              name="mode"
              checked={mode === "negative"}
              onChange={() => setMode("negative")}
            />
            Negative Only
          </label>
          <br />

          <label>
            <input
              type="radio"
              name="mode"
              checked={mode === "both"}
              onChange={() => setMode("both")}
            />
            Both (Radar = Positive)
          </label>
        </div>
      </div>

      {/* Radar Chart */}
      <div style={{ width: "100%", height: 500 }}>
        <Radar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};



  // ---------------------------
  // Overall chart (Graph 1)
  // Use group_polarity_summary.positive_percent & negative_percent which sum to ~100
  // ---------------------------

  const stackedOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom" },
  },
  scales: {
    x: { stacked: true },
    y: { stacked: true, beginAtZero: true },
  },
};


const renderOverallPosNegChart = () => {
  if (!surveyData?.group_polarity_summary?.length) {
    return <p className="no-data">No overall data available.</p>;
  }

  // ✅ FILTER: only groups having at least 1 MCQ question
  const validGroups = surveyData.group_polarity_summary.filter(
    g => Number(g.num_questions) > 0
  );

  if (!validGroups.length) {
    return <p className="no-data">No MCQ-based group data available.</p>;
  }

  const labels = validGroups.map(g => g.group);

  // =========================
  // MODE 1: Positive vs Negative
  // =========================
  if (overallMode === "posneg") {
    const posPercents = validGroups.map(g =>
      safeNum(g.positive_percent ?? 0)
    );

    const negPercents = validGroups.map(g =>
      safeNum(g.negative_percent ?? 0)
    );

    const data = {
      labels,
      datasets: [
        {
          label: "Positive",
          data: posPercents,
          backgroundColor: POS_COLOR,
          stack: "s"
        },
        {
          label: "Negative",
          data: negPercents,
          backgroundColor: NEG_COLOR,
          stack: "s"
        }
      ]
    };

    return (
      <div className="chart-wrapper" style={{ height: 480 }}>
        <Bar data={data} options={{ ...stackedOptions }} />
      </div>
    );
  }

  // =========================
  // MODE 2: SA / A / D / SD
  // =========================
  const SA = [];
  const A = [];
  const D = [];
  const SD = [];

  // IMPORTANT:
  // We aggregate from department_comparison
  // BUT only for groups that survived the MCQ filter
  const allowedGroupSet = new Set(labels);

  surveyData.department_comparison.forEach(groupBlock => {
    if (!allowedGroupSet.has(groupBlock.group)) return;

    let sa = 0, a = 0, d = 0, sd = 0;

    (groupBlock.levels || []).forEach(levelBlock => {
      Object.values(levelBlock.departments || {}).forEach(dep => {
        sa += dep["Strongly agree"]?.count || 0;
        a  += dep["Agree"]?.count || 0;
        d  += dep["Disagree"]?.count || 0;
        sd += dep["Strongly disagree"]?.count || 0;
      });
    });

    SA.push(sa);
    A.push(a);
    D.push(d);
    SD.push(sd);
  });

  const data = {
    labels,
    datasets: [
      { label: "Strongly Agree", data: SA, backgroundColor: "#A81026", stack: "likert" },
      { label: "Agree",          data: A,  backgroundColor: "#F3AC26", stack: "likert" },
      { label: "Disagree",       data: D,  backgroundColor: "#F15C41", stack: "likert" },
      { label: "Strongly Disagree", data: SD, backgroundColor: "#326AB3", stack: "likert" }
    ]
  };

  return (
    <div className="chart-wrapper" style={{ height: 480 }}>
      <Bar data={data} options={{ ...stackedOptions }} />
    </div>
  );
};
 
  // ---------------------------
  // Department charts (Graph 2)
  // Render one stacked chart per department using department_group_summary from API
  // ---------------------------
  const renderPerDepartmentCharts = () => {
    if (!surveyData || !Array.isArray(surveyData.department_group_summary) || surveyData.department_group_summary.length === 0) {
      return <p className="no-data">No department-wise group data available.</p>;
    }

    return surveyData.department_group_summary.map((deptBlock) => {
      const deptName = deptBlock.department;
      const groups = Array.isArray(deptBlock.groups) ? deptBlock.groups : [];

      // x labels are group names
      const labels = groups.map((g) => g.group);

      // positive/negative percent per (dept,group) — they are computed on backend as percent of pos+neg
      const posPercents = groups.map((g) => safeNum(g.positive_percent ?? 0));
      const negPercents = groups.map((g) => safeNum(g.negative_percent ?? 0));

      const data = {
        labels,
        datasets: [
          { label: "Positive", data: posPercents, backgroundColor: POS_COLOR, stack: "Stack 0" },
          { label: "Negative", data: negPercents, backgroundColor: NEG_COLOR, stack: "Stack 0" },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${safeNum(ctx.raw)}%`,
            },
          },
        },
        scales: {
          x: { stacked: true, ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 } },
          y: { stacked: true, beginAtZero: true, max: 100, title: { display: true, text: "% (per group in department)" } },
        },
      };

      return (
        <div className="chart-block" key={deptName}>
          <h3 className="group-subtitle">🏢 {deptName}</h3>
          <div className="chart-wrapper" style={{ height: 420 }}>
            <Bar data={data} options={options} />
          </div>
        </div>
      );
    });
  };

  // ---------------------------
// Graph 3 – Department comparison of positives only
// Groups on X-axis, departments as separate bars
// ---------------------------
const renderDepartmentPositiveComparisonChart = () => {
  const blocks = surveyData?.department_group_summary;
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return <p className="no-data">No department comparison data.</p>;
  }

  // All departments
  const departments = blocks.map((b) => b.department);

  // All groups (take from first department block)
  const groups = blocks[0].groups.map((g) => g.group);

  // For each department, build its dataset of positive%
  const datasets = blocks.map((deptBlock, idx) => ({
    label: deptBlock.department,
    data: deptBlock.groups.map((g) => g.positive_percent || 0),
    backgroundColor: [
       "#A81026", "#F3AC26", "#F15C41", "#326ab3",
    "#6C6E2E", "#3B231E", "#4CBFAB", "#29002D",
    ][idx % 7], // cycle colors
  }));

  const data = { labels: groups, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`,
        },
      },
    },
    scales: {
      x: { stacked: false, ticks: { autoSkip: false, maxRotation: 45 } },
      y: {
        stacked: false,
        beginAtZero: true,
        max: 100,
        title: { display: true, text: "Positive %" },
      },
    },
  };

  return (
    <div className="chart-block">
      {/* <h2 className="group-title">📈 Department-Wise Positive Comparison</h2> */}
      <div className="chart-wrapper" style={{ height: 480 }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};


if (loading) return <p className="loading-text">Loading survey analytics...</p>;
if (error) return <p className="error-text">{error}</p>;
if (!surveyData) return <p className="error-text">Survey not found.</p>;

// ✅ NO RESPONSE EDGE CASE
if (noResponses) {
  return (
    <div className={`survey-container ${surveyStatus === "inactive" ? "inactive-border" : isExpired ? "expired-border" : ""}`}>
      
      {/* HEADER STILL RENDERS */}
      <div className="survey-header">
        <h1>📊 Survey Analytics: {surveyData.survey_name}</h1>
        <div className="header-actions">
{user?.role === "SuperAdmin" && (
  <>
    <button className="export-btn" onClick={downloadExcel}>
      📄 Export Excel
    </button>

    <button
      className="invite-btn"
      disabled={!hasResponses}
      onClick={() =>
        navigate(`/superadmin/survey-generateppt/${surveyId}/company/${companyId}`)
      }
    >
      💻 Generate PPT
    </button>
  </>
)}

          <button
  className="invite-btn"
  type="button"
  onClick={() => {
    console.log("surveyId:", surveyId);
    console.log("companyId:", companyId);

    const url = `/admin/surveyinvite/${surveyId}/company/${companyId}/invite`;
    console.log("Navigating to:", url);

    navigate(url);
  }}
>
  📧 Invite
</button>


          {user?.role === "Admin" && (
            <button
              className={`status-btn ${surveyStatus === "active" ? "active" : "inactive"}`}
              onClick={toggleSurveyStatus}
              disabled={isExpired || updatingStatus}
            >
              {isExpired ? "⏳ Expired" : surveyStatus === "active" ? "🟢 Active" : "🔴 Inactive"}
            </button>
          )}
        </div>
      </div>

      {/* EMPTY STATE CARD */}
      <div className="no-response-card">
        <h2>📭 No Responses Yet</h2>
        <p>
          This survey has been created successfully, but no participant responses have been received yet.
        </p>

        <p className="no-response-sub">
          Once participants submit replies, analytics will appear automatically here.
        </p>

        <button
          className="invite-btn"
          onClick={() => navigate(`/admin/surveyinvite/${surveyId}/company/${companyId}/invite`)}
        >
          🚀 Invite Participants
        </button>
      </div>

    </div>
  );
}





  return (
    <div className={`survey-container ${surveyStatus === "inactive" ? "inactive-border" : isExpired ? "expired-border" : ""}`}>
      <div className="survey-header">
        <h1>📊 Survey Analytics: {surveyData.survey_name}</h1>
        <div className="header-actions">
          {user?.role === "SuperAdmin" && (
            <>
            <button className="export-btn" onClick={downloadExcel}>
      📄 Export Excel
    </button>
            
<button className="invite-btn" disabled={!hasResponses} onClick={() => navigate(`/superadmin/survey-generateppt/${surveyId}/company/${companyId}`) } > 💻 Generate PPT </button></>
)}

          <button className="invite-btn" onClick={() => navigate(`/admin/surveyinvite/${surveyId}/company/${companyId}/invite`)}>📧 Invite</button>
             {user?.role === "Admin" && (
          <button
            className={`status-btn ${surveyStatus === "active" ? "active" : "inactive"}`}
            onClick={toggleSurveyStatus}
            disabled={isExpired || updatingStatus}
          >
            {isExpired ? "⏳ Expired" : surveyStatus === "active" ? "🟢 Active" : "🔴 Inactive"}
          </button>
          )}
        </div>
      </div>

      <div className="participation-section">
        {typeof surveyData.participation_rate !== "undefined" && (
          <div className="participation-card">
            <strong>Participation:</strong> {safeNum(surveyData.participation_rate)}%
          </div>
        )}
      </div>

      <h2 className="group-title">📦 Department Response Distribution (Waffle Chart)</h2>

<WaffleChart
  data={surveyData.department_response_summary}
  squareSize={40}
  response_completion_summary ={ surveyData.response_completion_summary}
  department_response_completion_summary = {surveyData.department_response_completion_summary}
/>



      <h2 className="group-title">🏢 Radar: Group-wise Overview</h2>
      {renderRadarChart()}

      {/* Graph 1: overall groups */}
      <h2 className="group-title">📊 Overall Group Summary</h2>

<div className="overall-toggle">
  <label>
    <input
      type="radio"
      name="overallMode"
      checked={overallMode === "posneg"}
      onChange={() => setOverallMode("posneg")}
    />{" "}
    Positive vs Negative
  </label>

  <label style={{ marginLeft: 20 }}>
    <input
      type="radio"
      name="overallMode"
      checked={overallMode === "likert"}
      onChange={() => setOverallMode("likert")}
    />{" "}
    SA / A / D / SD
  </label>
</div>

      <div className="overall-section">{renderOverallPosNegChart()}</div>

<div className="section-box">
<h2 className="group-title">🏢 Department vs Groups — Comparison (Bar Chart)</h2>
{surveyData?.department_group_summary && (
  <DepartmentGroupBarCompare data={surveyData.department_group_summary} />
)}


      {/* Graph 2: department-wise charts — one chart per department */}
      {/* <div className="department-section">
        <h2 className="group-title">🏢 Department-wise: each chart shows all groups (Positive vs Negative)</h2>
        {renderPerDepartmentCharts()}
      </div> */}

      {/* Graph 3: department positive comparison */}
      <h2 className="group-title">📈 Department-Wise Positive Comparison </h2>
      <div className="department-section">
      {renderDepartmentPositiveComparisonChart()}
      </div>

<h2 className="group-title">🍩 Multi-Department Agreement Comparison (Pie Charts)</h2>
<MultiDepartmentAgreementPie
  departmentComparison={surveyData.department_comparison}
/>
<h2 className="group-title">📝 Text Responses</h2>
{/* TEXTBOX RESPONSE VIEWER */}
<TextboxResponseViewer textboxData={surveyData.textbox_response_summary} />



    </div>
    </div>
  );

};

export default AdminSurveyDetailPage;
